import {
  buildQualityReport,
  type PipelineBatchResult,
  type ValidationIssue,
  type GateVerdict,
} from '../../../../../../governance'
import { ALL_QUESTIONS_CORE, type Question } from '../../../../../../content'
import { resolveModuleCode } from './resolveModuleCode'

/**
 * UnifiedQuestionRow — view-model row combining pipeline result, quality
 * report, seed warnings and visual problem flag for one question.
 *
 * Consumers (Questions tab, top-blockers list, seed triage) read these rows
 * directly. Keep this stable — it is the public contract of the hook.
 */
export interface UnifiedQuestionRow {
  questionId: string
  familyId: string
  moduleCode: string
  tier: number
  skill: string
  questionType: string
  verdict: GateVerdict
  pipelineScore: number
  pipelineIssues: ValidationIssue[]
  blockerCount: number
  warningCount: number
  qualityScore: number
  cognitiveLevel: number
  seedWarnings: string[]
  hasVisualProblems: boolean
}

/**
 * Build unified rows from a PipelineBatchResult.
 *
 * Pure function — given the same batch result, returns the same rows in the
 * same order. The quality report is recomputed inside (covered by tests in
 * questionQualityEngine).
 */
export function buildUnifiedRows(
  batchResult: PipelineBatchResult,
): UnifiedQuestionRow[] {
  // Build quality lookup once
  const qualityReport = buildQualityReport(ALL_QUESTIONS_CORE)
  const qualityMap = new Map(qualityReport.scores.map((s) => [s.questionId, s]))

  // O(1) question lookup (was O(n²) before R1's predecessor fix)
  const questionLookup = new Map<string, Question>(
    ALL_QUESTIONS_CORE.map((q) => [q.id, q]),
  )

  let missingCount = 0
  const missingIds: string[] = []

  const rows = batchResult.results.map((result) => {
    const questionId = result.questionId
    const question = questionLookup.get(questionId)
    if (!question) {
      missingCount++
      if (missingIds.length < 10) missingIds.push(questionId)
    }
    const moduleCode = resolveModuleCode(question, questionId)
    const quality = qualityMap.get(questionId)

    const blockers = result.gate.issues.filter((i) => i.kind === 'blocker')
    const warnings = result.gate.issues.filter((i) => i.kind === 'coach')
    const hasVisualProblems = result.gate.issues.some(
      (i) => i.domain === 'visual',
    )

    return {
      questionId,
      familyId: question?.familyId || '',
      moduleCode,
      tier: question?.tier || 0,
      skill: question?.skill || '',
      questionType: question?.questionType || 'unknown',
      verdict: result.gate.verdict,
      pipelineScore: result.gate.score,
      pipelineIssues: result.gate.issues,
      blockerCount: blockers.length,
      warningCount: warnings.length,
      qualityScore: quality?.totalScore || 0,
      cognitiveLevel: quality?.dimensions.cognitiveLevel || 0,
      seedWarnings: result.gate.issues
        .filter((i) => i.domain === 'seed')
        .map((i) => i.message),
      hasVisualProblems,
    }
  })

  if (missingCount > 0 && typeof console !== 'undefined') {
    const sample = missingIds.join(', ')
    const suffix =
      missingCount > missingIds.length
        ? `, …(+${missingCount - missingIds.length} more)`
        : ''
    console.warn(
      `[governance] buildUnifiedRows: ${missingCount} pipeline result(s) could not be matched against ALL_QUESTIONS_CORE — these will show type "unknown" in the Vragen tab. Likely cause: stale snapshot or out-of-sync content build. Sample IDs: ${sample}${suffix}`,
    )
  }

  return rows
}
